import { Button, LoadingButton, SnackbarManager } from '@cmg/design-system';
import { Form, FormikProvider, useFormik, validateYupSchema, yupToFormErrors } from 'formik';
import isEmpty from 'lodash/isEmpty';
import React, { FC, useEffect, useMemo } from 'react';

import ServerErrorsBanner from '../../../../../../common/components/indicators/server-error/ServerErrorsBanner';
import { ContactSource } from '../../../../../../graphql';
import { updateDemandGridRowsReactiveVar } from '../../../../../order-book/institutional-demand/hooks/useUpdateDemandGridRowsReactiveVar';
import {
  IndicationForm_IndicationPartsFragment,
  IndicationForm_OfferingPartsFragment,
} from '../indication-form/graphql/__generated__';
import { useAssignContactsMutation } from './hooks/useAssignContactsMutation';
import { useContactsQuery } from './hooks/useContactsQuery';
import {
  contactsValidationSchema,
  InstitutionalIndicationType,
  InvestorContactValues,
  mapToFormValues,
  mapValuesToInput,
} from './InvestorContactInformationForm.model';
import InvestorContactInformation from './layout/InvestorContactInformation';

export type Props = {
  isCoveredAccount: boolean;
  setDisplayMissingContacts: (value: boolean) => void;
  indication: InstitutionalIndicationType & {
    investorInformation?: IndicationForm_IndicationPartsFragment['investorInformation'];
  };
  offering: IndicationForm_OfferingPartsFragment;
  investorContactMandatory?: boolean;
  isInvestorCoveredAndEditable?: boolean;
  isEditingInvestorContacts: boolean;
  setIsEditingInvestorContacts: (value: boolean) => void;
  isInvestorContactRequired: boolean;
};

const InvestorContactInformationForm: FC<Props> = ({
  isCoveredAccount,
  setDisplayMissingContacts,
  indication,
  offering,
  investorContactMandatory = false,
  isInvestorCoveredAndEditable,
  isEditingInvestorContacts,
  setIsEditingInvestorContacts,
  isInvestorContactRequired,
}) => {
  const { contacts, loading, error } = useContactsQuery(
    {
      indicationId: indication.id,
      offeringId: offering.id,
    },
    isCoveredAccount
  );
  const [assignContacts, { loading: assignContactsLoading }] =
    useAssignContactsMutation(isCoveredAccount);

  const { buySideContacts, sellSideContacts } = useMemo(
    () => ({
      buySideContacts:
        contacts?.contacts
          .filter(item => item.source === ContactSource.BuySide)
          .map(mapToFormValues) ?? [],
      sellSideContacts:
        contacts?.contacts
          .filter(item => item.source === ContactSource.SellSide)
          .map(mapToFormValues) ?? [],
    }),
    [contacts]
  );

  const formik = useFormik<InvestorContactValues>({
    validateOnChange: true,
    validateOnBlur: false,
    validateOnMount: false,
    initialValues: {
      buySideContacts,
      sellSideContacts,
    },
    onSubmit: async (values, { setValues }) => {
      try {
        const { data } = await assignContacts({
          variables: {
            indicationId: indication.id,
            offeringId: offering.id,
            input: mapValuesToInput(values.sellSideContacts),
          },
        });

        SnackbarManager.success('Successfully updated Investor contact information');
        await setValues(
          Object.assign(values, {
            sellSideContacts: values.sellSideContacts.filter(
              item => item.email !== '' && item.name !== ''
            ),
          })
        ); // remove empty rows from formik
        setIsEditingInvestorContacts(false);

        if (!data) {
          return;
        }

        const { indicationId } =
          'assignContacts' in data ? data.assignContacts : data.assignCoveredContacts;
        updateDemandGridRowsReactiveVar({ fetchIds: [indicationId] });
      } catch {
        SnackbarManager.error('Failed to update Investor contact information');
      }
    },
    enableReinitialize: true,
    validate: async values => {
      try {
        await validateYupSchema(values, contactsValidationSchema, false);
        return {};
      } catch (err) {
        return yupToFormErrors(err);
      }
    },
  });

  useEffect(() => {
    if (!loading) {
      setDisplayMissingContacts(
        isEmpty(sellSideContacts) && isEmpty(buySideContacts) && isInvestorContactRequired
      );
    }
  }, [
    sellSideContacts,
    buySideContacts,
    isInvestorContactRequired,
    setDisplayMissingContacts,
    loading,
    contacts,
  ]);

  return (
    <FormikProvider value={formik}>
      {error && <ServerErrorsBanner error={error} />}
      <Form>
        <InvestorContactInformation
          investorContactMandatory={investorContactMandatory}
          bankInvestorKey={indication.investorInformation?.bankInvestorKey}
          buySideContacts={formik.values.buySideContacts}
          sellSideContacts={formik.values.sellSideContacts}
          editing={isEditingInvestorContacts}
          bankInvestorName={indication.investorInformation?.bankInvestorName}
          saving={assignContactsLoading}
          loading={loading}
          isCoveredAccount={isCoveredAccount}
          actions={[
            ...(isEditingInvestorContacts
              ? [
                  <Button
                    onClick={() => {
                      formik.resetForm();
                      setIsEditingInvestorContacts(false);
                    }}
                    disabled={loading}
                    variant="outlined"
                    key="cancel"
                  >
                    Cancel
                  </Button>,
                  <LoadingButton
                    variant="contained"
                    onClick={() => formik.handleSubmit()}
                    loading={assignContactsLoading}
                    key="save"
                  >
                    Save
                  </LoadingButton>,
                ]
              : []),
            ...(!isEditingInvestorContacts && isInvestorCoveredAndEditable
              ? [
                  <Button
                    key="edit"
                    variant="outlined"
                    onClick={() => setIsEditingInvestorContacts(true)}
                  >
                    Edit
                  </Button>,
                ]
              : []),
          ]}
        />
      </Form>
    </FormikProvider>
  );
};

export default InvestorContactInformationForm;
